import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Orbit } from "lucide-react";

const links = [
  { to: "/", label: "Home", id: "nav-link-home" },
  { to: "/businesses", label: "For Businesses", id: "nav-link-businesses" },
  { to: "/creators", label: "For Creators", id: "nav-link-creators" },
  { to: "/how-it-works", label: "How It Works", id: "nav-link-how-it-works" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
      style={{
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        background: scrolled ? "rgba(3,7,18,0.82)" : "rgba(3,7,18,0.5)",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
      }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <span className="grid place-items-center h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 shadow-lg shadow-blue-900/40">
              <Orbit size={18} className="text-white" />
            </span>
            <span className="font-display font-bold text-lg tracking-tight text-white">Neo Orbit</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {links.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.id}
                  className={`px-3.5 py-2 text-sm rounded-full transition-colors ${
                    active ? "text-white bg-white/5" : "text-slate-400 hover:text-white"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Link to="/business-apply" data-testid="nav-cta-beta" className="hidden sm:inline-flex btn-primary !py-2 !px-4 !text-sm">
              Join Private Beta
            </Link>
            <button
              className="md:hidden grid place-items-center h-10 w-10 rounded-lg border border-white/10 text-slate-200"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-white/10"
            style={{ background: "rgba(3,7,18,0.95)" }}
          >
            <div className="px-4 py-4 space-y-1">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.id}-mobile`}
                  className="block px-4 py-3 rounded-xl text-slate-200 hover:bg-white/5"
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/business-apply" className="btn-primary w-full mt-2">Join Private Beta</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
